import React, { useEffect, useState } from "react";
import styles from "./PlaydateInviteBaseUnit.module.css";
import downArrowIcon from "../../assets/icons/downArrow.svg";
import unseenIcon from "../../assets/icons/unseenIcon.svg";
import {
    InboxItemType,
    InviteStatus,
    ParticipantSummary,
    PlaydateInvite,
    SeenStatus,
    User,
    UserSummary,
    useUpdateInboxSeenStatusMutation
} from "../../gql/types";
import noUser from "../../assets/no-user.png";
import InfoTag, { TagVariant } from "./InfoTag";
import ActionButton from "../button/ActionButton";
import { formatFinalScheduleOption, formatFirstScheduleOption } from "./formatter";
import upArrowIcon from "../../assets/icons/upArrow.svg";
import { useInboxUpdates } from "../../contexts/InboxUpdatesContext";
import PlaydateInviteDetails from "./PlaydateInviteDetails";
import PlaydateInviteOrganizerReviewWorkflow from "./workflow/PlaydateInviteOrganizerReviewWorkflow";
import PlaydateInviteRecipientReviewWorkflow from "./workflow/recipient/review/PlaydateInviteRecipientReviewWorkflow";
import { useLayout } from "../layout/LayoutContext";
import { useToastContext } from "contexts/ToastContext";

interface PlaydateInviteBaseUnitProps {
    user?: User;
    invite: PlaydateInvite;
    isNew: boolean;
    isInbound: boolean;
    infoTags: Array<{ label: string; variant: TagVariant }>;
    representedPerson?: ParticipantSummary;
    otherGuestsCount: number;
    onResponse: () => void;
}

const PlaydateInviteBaseUnit: React.FC<PlaydateInviteBaseUnitProps> = ({
    user,
    invite,
    isNew,
    isInbound,
    infoTags,
    representedPerson,
    otherGuestsCount,
    onResponse
}) => {
    const currentInfoTags = [...infoTags];

    const [expanded, setExpanded] = useState(false);
    const [showRecipientView, setShowRecipientView] = useState(false);
    const [hasUpdatedSeen, setHasUpdatedSeen] = useState(false);
    const [localIsNew, setLocalIsNew] = useState(isNew);
    const [updateSeenStatus] = useUpdateInboxSeenStatusMutation();
    const { addOptimisticUpdate } = useInboxUpdates();
    const { baseZIndex } = useLayout();
    const { triggerToastEvent } = useToastContext();

    const handleViewDetailsClick = () => {
        setShowRecipientView(true);
    };

    const handleCloseRecipientView = () => {
        setShowRecipientView(false);
    };

    const handleCompleteResponse = (success: boolean, _updatedInvite?: PlaydateInvite) => {
        if (success) {
            triggerToastEvent();
            onResponse();
        }
        setShowRecipientView(false);
    };

    const handleExpandClick = () => {
        const newExpanded = !expanded;
        setExpanded(newExpanded);

        if (newExpanded && isNew && !hasUpdatedSeen) {
            if (isInbound && invite.guestUpdate?.updateID) {
                setLocalIsNew(false);
                addOptimisticUpdate({
                    updateID: invite.guestUpdate.updateID,
                    itemType: InboxItemType.OutboundPlaydate,
                    seenStatus: SeenStatus.Read
                });
                updateSeenStatus({
                    variables: {
                        input: invite.guestUpdate.updateID,
                        itemType: InboxItemType.InboundPlaydate
                    }
                })
                    .then(() => setHasUpdatedSeen(true))
                    .catch((err) => console.error("Error updating seen status:", err));
            }

            if (!isInbound && invite.organizerUpdate?.updateID) {
                setLocalIsNew(false);
                addOptimisticUpdate({
                    updateID: invite.organizerUpdate.updateID,
                    itemType: InboxItemType.OutboundPlaydate,
                    seenStatus: SeenStatus.Read
                });
                updateSeenStatus({
                    variables: {
                        input: invite.organizerUpdate.updateID,
                        itemType: InboxItemType.OutboundPlaydate
                    }
                })
                    .then(() => setHasUpdatedSeen(true))
                    .catch((err) => console.error("Error updating seen status:", err));
            }
        }
    };

    // Type guard for UserSummary
    function isUserSummary(participant: ParticipantSummary): participant is UserSummary {
        return "email" in participant;
    }

    const representedPersonProfileUrl = representedPerson
        ? isUserSummary(representedPerson)
            ? representedPerson.profilePictureUrl
            : representedPerson.parents?.[0]?.profilePictureUrl
        : undefined;

    const representedPersonDisplayName = representedPerson
        ? isUserSummary(representedPerson)
            ? representedPerson.firstName
            : representedPerson.name
        : "";

    let date: string;
    let time: string;
    if (invite.status === InviteStatus.Confirmed) {
        const { date: optionDate, time: optionTime } = formatFinalScheduleOption(invite.finalScheduleOption);
        date = optionDate;
        time = optionTime;
    } else {
        const { date: optionDate, time: optionTime } = formatFirstScheduleOption(invite.scheduleOptions);
        date = optionDate;
        time = optionTime;
    }

    return (
        <div className={`${styles.playdateInviteUnit} ${invite.expired || invite.status === InviteStatus.Abandoned ? styles.expired : ''}`}>
            <div className={styles.mainUnit} onClick={handleExpandClick}>
                <div className={styles.topRow}>
                    <div className={styles.tagRow}>
                        {currentInfoTags.map((tagData, idx) => (
                            <InfoTag
                                key={`${invite.inviteID}_${idx}`}
                                label={tagData.label}
                                variant={tagData.variant}
                            />
                        ))}
                    </div>
                    {localIsNew && <img src={unseenIcon} className={styles.newIndicator} />}
                </div>

                <div className={styles.mainRow}>
                    <div className={styles.leftSection}>
                        <img
                            src={representedPersonProfileUrl || noUser}
                            alt="RepresentedPerson"
                            className={styles.profilePic}
                        />
                        <div className={styles.organizerName}>{representedPersonDisplayName}</div>
                    </div>

                    <div className={styles.rightSection}>
                        {otherGuestsCount === 1 ? (
                            <span className={styles.otherGuests}>and 1 other</span>
                        ) : otherGuestsCount > 1 ? (
                            <span className={styles.otherGuests}>and {otherGuestsCount} others</span>
                        ) : null}
                        <button className={styles.expandButton} onClick={handleExpandClick}>
                            {!expanded ? (
                                <img src={downArrowIcon} alt="Expand" />
                            ) : (
                                <img src={upArrowIcon} alt="Shrink" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {expanded && (
                <div className={styles.expandedDetails}>
                    <PlaydateInviteDetails
                        invite={invite}
                    />
                    <ActionButton onClick={handleViewDetailsClick} className={styles.viewDetailsButton}>
                        View Details
                    </ActionButton>
                </div>
            )}

            {showRecipientView && isInbound && (
                <PlaydateInviteRecipientReviewWorkflow
                    user={user}
                    onBack={handleCloseRecipientView}
                    invite={invite}
                    onDataChange={handleCompleteResponse}
                    baseZIndex={baseZIndex}
                />
            )}

            {showRecipientView && !isInbound && (
                <PlaydateInviteOrganizerReviewWorkflow
                    onBack={handleCloseRecipientView}
                    invite={invite}
                    onDataChange={handleCompleteResponse}
                    baseZIndex={baseZIndex}
                />
            )}
        </div>
    );
};

export default PlaydateInviteBaseUnit;
